import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { LinkCtaNext } from '~/components-v2'
import { Person } from '~/types'
import { Link } from 'react-router'
import type { Breadcrumb } from './breadcrumbs'
import { Byline } from './byline'

type TocItem = {
  id: string
  label: string
}

type SidebarCtaStyle = CSSProperties & {
  '--blog-post-toc-count'?: number
}

const headingToId = (heading: HTMLHeadingElement, index: number) => {
  if (heading.id) return heading.id

  const id = `section-${index + 1}`
  heading.id = id
  return id
}

const headingToLabel = (heading: HTMLHeadingElement) => {
  const headingClone = heading.cloneNode(true) as HTMLHeadingElement
  headingClone.querySelectorAll('.anchor-reveal, .anchor').forEach((anchor) => {
    anchor.remove()
  })

  return headingClone.textContent?.trim() ?? ''
}

const scrollToHeading = (id: string) => {
  const heading = document.getElementById(id)
  if (!heading) return

  const styles = window.getComputedStyle(document.documentElement)
  const headerHeight =
    Number.parseFloat(styles.getPropertyValue('--header-height')) || 0
  const gap = Number.parseFloat(styles.getPropertyValue('--size8')) || 0
  const top = heading.getBoundingClientRect().top + window.scrollY

  window.history.pushState(null, '', `#${id}`)
  window.scrollTo({
    behavior: 'smooth',
    top: Math.max(top - headerHeight - gap, 0),
  })
}

export const BlogPostSidebar = ({
  authors,
  breadcrumbs,
  date,
  postKey,
  title,
}: {
  authors: Person[]
  breadcrumbs: Breadcrumb[]
  date: Date | null
  postKey: string
  title: string
}) => {
  const [activeId, setActiveId] = useState<string>()
  const [isTitleAnimating, setIsTitleAnimating] = useState(false)
  const [isTitleVisible, setIsTitleVisible] = useState(false)
  const [tocItems, setTocItems] = useState<TocItem[]>([])
  const animationTimeoutRef = useRef<number | null>(null)
  const frameRef = useRef<number | null>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const tocRef = useRef<HTMLElement>(null)
  const titleVisibleRef = useRef(false)
  const [prevPostKey, setPrevPostKey] = useState(postKey)

  if (postKey !== prevPostKey) {
    setPrevPostKey(postKey)
    setActiveId(undefined)
    setIsTitleAnimating(false)
    setIsTitleVisible(false)
    setTocItems([])
  }

  useEffect(() => {
    titleVisibleRef.current = false

    const titleElement = document.querySelector<HTMLElement>('.blog-post h1')
    const article = document.querySelector<HTMLElement>('.blog-post > article')
    if (!titleElement || !article) return

    const headings = Array.from(
      article.querySelectorAll<HTMLHeadingElement>('h2'),
    )

    const updateToc = () => {
      const items = headings.map((heading, index) => ({
        id: headingToId(heading, index),
        label: headingToLabel(heading),
      }))

      setTocItems(items.filter((item) => item.label.length > 0))
      setActiveId(items[0]?.id)
    }

    const triggerOffset = () => {
      const styles = window.getComputedStyle(document.documentElement)
      const headerHeight =
        Number.parseFloat(styles.getPropertyValue('--header-height')) || 0
      const stickyGap =
        Number.parseFloat(styles.getPropertyValue('--size10')) || 0

      return headerHeight + stickyGap - 12
    }

    const updateTitleState = () => {
      frameRef.current = null

      const sourceRect = titleElement.getBoundingClientRect()
      const shouldShowTitle = sourceRect.bottom <= triggerOffset()

      if (shouldShowTitle && !titleVisibleRef.current && titleRef.current) {
        setIsTitleAnimating(true)

        if (animationTimeoutRef.current) {
          window.clearTimeout(animationTimeoutRef.current)
        }

        animationTimeoutRef.current = window.setTimeout(
          () => setIsTitleAnimating(false),
          860,
        )
      }

      if (!shouldShowTitle && titleVisibleRef.current) {
        setIsTitleAnimating(false)
      }

      titleVisibleRef.current = shouldShowTitle
      setIsTitleVisible(shouldShowTitle)
    }

    const scheduleTitleUpdate = () => {
      if (frameRef.current) return
      frameRef.current = window.requestAnimationFrame(updateTitleState)
    }

    updateToc()
    updateTitleState()
    window.addEventListener('scroll', scheduleTitleUpdate, { passive: true })
    window.addEventListener('resize', scheduleTitleUpdate)

    const headingObserver = new IntersectionObserver(
      (entries) => {
        // Indexed rather than .at(0), which readers on older browsers lack.
        const visibleHeading = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
          )[0]

        if (visibleHeading?.target.id) {
          setActiveId(visibleHeading.target.id)
        }
      },
      {
        rootMargin: '-25% 0px -60% 0px',
        threshold: 0,
      },
    )

    headings.forEach((heading) => headingObserver.observe(heading))

    return () => {
      if (animationTimeoutRef.current) {
        window.clearTimeout(animationTimeoutRef.current)
      }
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current)
      }
      window.removeEventListener('scroll', scheduleTitleUpdate)
      window.removeEventListener('resize', scheduleTitleUpdate)
      headingObserver.disconnect()
    }
  }, [postKey])

  useEffect(() => {
    if (!activeId || !tocRef.current) return

    const activeLink = tocRef.current.querySelector<HTMLAnchorElement>(
      `a[href="#${CSS.escape(activeId)}"]`,
    )

    if (!activeLink) return

    const targetTop =
      activeLink.offsetTop -
      tocRef.current.clientHeight / 2 +
      activeLink.clientHeight / 2

    tocRef.current.scrollTo({
      behavior: 'smooth',
      top: Math.max(targetTop, 0),
    })
  }, [activeId])

  const parent = breadcrumbs[breadcrumbs.length - 2]

  return (
    <aside
      aria-label="Post navigation"
      className={`blog-post-sidebar ${
        isTitleVisible ? 'is-title-visible' : ''
      } ${isTitleAnimating ? 'is-title-animating' : ''}`}
    >
      <div className="blog-post-sidebar__rail" aria-hidden="true" />
      <div className="blog-post-sidebar__inner">
        <div className="blog-post-sidebar__reveal blog-post-sidebar__reveal--title">
          <div
            aria-hidden="true"
            className="blog-post-sidebar__title heading-m text-brand-dark"
            ref={titleRef}
          >
            {title}
          </div>
        </div>
        <Byline authors={authors} date={date} />
        {tocItems.length > 0 && (
          <div className="blog-post-sidebar__reveal blog-post-sidebar__reveal--toc">
            <nav
              className="blog-post-toc"
              aria-label="Table of contents"
              ref={tocRef}
            >
              <ul>
                {tocItems.map((item) => (
                  <li key={item.id}>
                    <a
                      className={activeId === item.id ? 'is-active' : undefined}
                      href={`#${item.id}`}
                      onClick={(event) => {
                        event.preventDefault()
                        scrollToHeading(item.id)
                      }}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}
        {parent && (
          <Link className="blog-post-sidebar__back" to={parent.path}>
            <span className="blog-post-sidebar__back-icon-wrap">
              <svg
                aria-hidden="true"
                className="blog-post-sidebar__back-icon"
                fill="none"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="9.2859"
                  stroke="currentColor"
                  strokeWidth="1.4282"
                />
                <path
                  d="M11.7178 6.12598L8.35889 9.48487L11.7178 12.8438"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.4282"
                />
              </svg>
            </span>
            Back to {parent.name}
          </Link>
        )}
        {tocItems.length > 0 && (
          <div
            className="blog-post-sidebar__cta"
            style={
              { '--blog-post-toc-count': tocItems.length } as SidebarCtaStyle
            }
          >
            <LinkCtaNext to="/subscribe">Get Omni updates</LinkCtaNext>
          </div>
        )}
      </div>
    </aside>
  )
}
